import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { cropAPI } from '../services/api';
import LanguageSelector from './LanguageSelector';
import labels from '../constants/labels.json';
import './VarietyPage.css';

const VarietyPage = () => {
  const { varietyCode } = useParams();
  const {
    currentLanguage,
    currentCrop,
    loading,
    error,
    setCurrentCrop,
    setLoading,
    setError,
    getImageUrl
  } = useStore();

  const [imageError, setImageError] = useState(false);

  const fetchCropData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cropAPI.getCropByCode(varietyCode, currentLanguage);
      setCurrentCrop(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Crop not found');
      setCurrentCrop(null);
    } finally {
      setLoading(false);
    }
  }, [varietyCode, currentLanguage, setLoading, setError, setCurrentCrop]);

  useEffect(() => {
    fetchCropData();
  }, [fetchCropData]);

  // Helper function to get translated label
  const getLabel = (key) => {
    return labels[key]?.[currentLanguage] || labels[key]?.en || '';
  };

  const practicesData = [
    { key: 'eligibleStates', labelKey: 'states', field: 'eligibleStatesMl' },
    { key: 'landPreparation', labelKey: 'land', field: 'landPreparationMl' },
    { key: 'seedProcessing', labelKey: 'processing', field: 'seedProcessingMl' },
    { key: 'sowingTime', labelKey: 'sowing', field: 'sowingTimeMl' },
    { key: 'seedRate', labelKey: 'seedrate', field: 'seedRateMl' },
    { key: 'fertilizers', labelKey: 'fertilizer', field: 'fertilizersMl' },
    { key: 'weedControl', labelKey: 'weed', field: 'weedControlMl' },
    { key: 'irrigation', labelKey: 'irrigation', field: 'irrigationMl' },
    { key: 'harvesting', labelKey: 'extharvesting', field: 'harvestingMl' }
  ];

  // Helper function to extract value from ML fields
  const getMlValue = (field) => {
    if (!field) return null;
    // ML fields come as { "value": "text" } from the API
    return field.value || null;
  };

  if (loading) {
    return (
      <div className="variety-page">
        <div className="loading">Loading crop information...</div>
      </div>
    );
  }

  if (error || !currentCrop) {
    return (
      <div className="variety-page">
        <div className="error">
          <h2>Seed details not found</h2>
          <p>{error || 'The requested variety code was not found.'}</p>
        </div>
      </div>
    );
  }

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="variety-page">
      {/* Header with logo and language selector */}
      <header className="page-header">
        <div className="variety-header">
          <div className="logo">
            <a href="https://www.namdhariseeds.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3">
              <div className="ns-logo">
                <img src="/logo.png" alt="Namdhari Seeds Logo" className="w-10 h-10" />
              </div>
              <div className="company-info">
                <span className="company-name">Namdhari Seeds</span>
                <span className="admin-text">Seeds for a better future</span>
              </div>
            </a>
          </div>

        </div>
        <LanguageSelector />
      </header>

      {/* Seed packet display */}
      <div className="seed-packet-section">
        <div className="seed-packet">
          <div className="packet-header">
            <div className="logo">
              <div className="ns-logo">
                <img src="/logo.png" alt="Namdhari Seeds Logo" className="w-10 h-10" />
              </div>
              <div className="company-info">
                <span className="company-name">Namdhari Seeds</span>
                <span className="admin-text">Seeds for a better future</span>
              </div>
            </div>
            <div className="category">Vegetable Seeds</div>
          </div>

          <div className="variety-code-banner">
            {currentCrop.varietyCode}
          </div>

          <div className="seed-image">
            {!imageError && currentCrop.pouchImageName ? (
              <img
                src={getImageUrl(currentCrop.pouchImageName)}
                alt={`${currentCrop.cropName} ${currentCrop.varietyCode}`}
                onError={handleImageError}
              />
            ) : (
              <div className="no-image">
                <div className="placeholder-icon">🌱</div>
                <span>No Image Available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Package of Practices */}
      <div className="practices-section">
        <h1 className="practices-title">{getLabel('pop').toUpperCase()}</h1>

        <div className="crop-info">
          <div className="crop-detail">
            <span className="number">1.</span>
            <span className="label">{getLabel('crop')} :</span>
            <span className="value">{getMlValue(currentCrop.cropNameMl) || currentCrop.cropName}</span>
          </div>
          <div className="crop-detail">
            <span className="number">2.</span>
            <span className="label">{getLabel('variety')} :</span>
            <span className="value">{getMlValue(currentCrop.varietyNameMl) || currentCrop.varietyCode}</span>
          </div>
        </div>

        <div className="practices-table">
          <div className="table-header">
            <div className="serial-header">S. NO</div>
            <div className="instruction-header">INSTRUCTIONS</div>
            <div className="practices-header">PRACTICES</div>
          </div>

          {practicesData.map((practice, index) => (
            <div key={practice.key} className="table-row">
              <div className="serial-number">{index + 1}.</div>
              <div className="instruction">{getLabel(practice.labelKey)}</div>
              <div className="practice-content">
                {getMlValue(currentCrop[practice.field]) || 'Information not available'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="page-footer">
        <div className="footer-links">
          <span>Explore our other products - </span>
          <a href="https://namdhariseeds.com/seeds" target="_blank" rel="noopener noreferrer" className="catalogue-link">NAMDHARI SEEDS CATALOGUE</a>
        </div>
        <div className="disclaimer">
          <span className="disclaimer-text">
            Disclaimer - Crop duration and crop yield may vary depending upon climatic conditions, geography,
            soil type, pests, diseases, agronomic practices etc
          </span>
        </div>
      </footer>
    </div>
  );
};

export default VarietyPage;